/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  CreateSkuRuleRequest,
  DeleteAdminV1OrgSkuRulesIdPathParameters,
  GetAdminV1OrgSkuRules200,
  GetAdminV1OrgSkuRulesIdPathParameters,
  GetAdminV1OrgSkuRulesIdRuns200,
  GetAdminV1OrgSkuRulesIdRunsPathParameters,
  GetAdminV1OrgSkuRulesIdRunsRunIdPathParameters,
  GetAdminV1OrgSkuRulesResolveParams,
  PatchAdminV1OrgSkuRulesIdPathParameters,
  PostAdminV1OrgSkuRulesIdApplyPathParameters,
  SkuRule,
  SkuRuleApplyRequest,
  SkuRulePreview,
  SkuRulePreviewRequest,
  SkuRuleResolve,
  SkuRuleRun,
  UpdateSkuRuleRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Each rule claims product types and holds the templates style numbers and variant SKUs are written from (D-161). At most 50 per organization, returned whole
 * @summary SKU rules of the organization
 */
export const getAdminV1OrgSkuRules = (

 options?: SecondParameter<typeof customInstance<GetAdminV1OrgSkuRules200>>,) => {
      return customInstance<GetAdminV1OrgSkuRules200>(
      {url: `/admin/v1/org/sku-rules`, method: 'GET'
    },
      options);
    }
  /**
 * Template grammar errors answer 422 SKU_TEMPLATE_INVALID with one detail per problem and meta.problems; warnings are saved through and returned on the rule. A product type another rule claims answers 409 PRODUCT_TYPE_CLAIMED with meta.ruleId. Making a rule the default takes the flag off the previous default
 * @summary Create a SKU rule
 */
export const postAdminV1OrgSkuRules = (
    createSkuRuleRequest: BodyType<CreateSkuRuleRequest>,
 options?: SecondParameter<typeof customInstance<SkuRule>>,) => {
      return customInstance<SkuRule>(
      {url: `/admin/v1/org/sku-rules`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createSkuRuleRequest
    },
      options);
    }
  /**
 * With productId the real product and its variants are rendered (its stored sequence, or the counter's next number); otherwise synthetic combinations of the product type's option attributes, two values per axis. The counter is never consumed. problems carries grammar errors and rule warnings
 * @summary Render sample style numbers and SKUs for unsaved templates
 */
export const postAdminV1OrgSkuRulesPreview = (
    skuRulePreviewRequest: BodyType<SkuRulePreviewRequest>,
 options?: SecondParameter<typeof customInstance<SkuRulePreview>>,) => {
      return customInstance<SkuRulePreview>(
      {url: `/admin/v1/org/sku-rules/preview`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: skuRulePreviewRequest
    },
      options);
    }
  /**
 * @summary Which rule a product of this type gets and what its next style number looks like
 */
export const getAdminV1OrgSkuRulesResolve = (
    params?: GetAdminV1OrgSkuRulesResolveParams,
 options?: SecondParameter<typeof customInstance<SkuRuleResolve>>,) => {
      return customInstance<SkuRuleResolve>(
      {url: `/admin/v1/org/sku-rules/resolve`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Get a SKU rule
 */
export const getAdminV1OrgSkuRulesId = (
    { id }: GetAdminV1OrgSkuRulesIdPathParameters,
 options?: SecondParameter<typeof customInstance<SkuRule>>,) => {
      return customInstance<SkuRule>(
      {url: `/admin/v1/org/sku-rules/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * productTypeIds replaces the claims; counters move forward only (422 below the current number). Existing style numbers and SKUs are untouched
 * @summary Update a SKU rule
 */
export const patchAdminV1OrgSkuRulesId = (
    { id }: PatchAdminV1OrgSkuRulesIdPathParameters,
    updateSkuRuleRequest: BodyType<UpdateSkuRuleRequest>,
 options?: SecondParameter<typeof customInstance<SkuRule>>,) => {
      return customInstance<SkuRule>(
      {url: `/admin/v1/org/sku-rules/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateSkuRuleRequest
    },
      options);
    }
  /**
 * Products keep their style numbers and SKUs; their skuRule becomes null
 * @summary Delete a SKU rule
 */
export const deleteAdminV1OrgSkuRulesId = (
    { id }: DeleteAdminV1OrgSkuRulesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/sku-rules/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Walks the rule's products in batches of 50 with the same logic as a per-product regenerate: variants on an order, in a cart or stocked are never touched. scope missing fills null roots and SKUs only; generated also rewrites generated values; all rewrites manual values too and needs includeManual. dryRun counts and writes nothing. One run per rule at a time (409)
 * @summary Apply the rule to its existing products on the worker
 */
export const postAdminV1OrgSkuRulesIdApply = (
    { id }: PostAdminV1OrgSkuRulesIdApplyPathParameters,
    skuRuleApplyRequest: BodyType<SkuRuleApplyRequest>,
 options?: SecondParameter<typeof customInstance<SkuRuleRun>>,) => {
      return customInstance<SkuRuleRun>(
      {url: `/admin/v1/org/sku-rules/${id}/apply`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: skuRuleApplyRequest
    },
      options);
    }
  /**
 * @summary Apply runs of a rule, newest first (at most 20)
 */
export const getAdminV1OrgSkuRulesIdRuns = (
    { id }: GetAdminV1OrgSkuRulesIdRunsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgSkuRulesIdRuns200>>,) => {
      return customInstance<GetAdminV1OrgSkuRulesIdRuns200>(
      {url: `/admin/v1/org/sku-rules/${id}/runs`, method: 'GET'
    },
      options);
    }
  /**
 * @summary One apply run with its progress counts
 */
export const getAdminV1OrgSkuRulesIdRunsRunId = (
    { id, runId }: GetAdminV1OrgSkuRulesIdRunsRunIdPathParameters,
 options?: SecondParameter<typeof customInstance<SkuRuleRun>>,) => {
      return customInstance<SkuRuleRun>(
      {url: `/admin/v1/org/sku-rules/${id}/runs/${runId}`, method: 'GET'
    },
      options);
    }
  export type GetAdminV1OrgSkuRulesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSkuRules>>>
export type PostAdminV1OrgSkuRulesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSkuRules>>>
export type PostAdminV1OrgSkuRulesPreviewResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSkuRulesPreview>>>
export type GetAdminV1OrgSkuRulesResolveResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSkuRulesResolve>>>
export type GetAdminV1OrgSkuRulesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSkuRulesId>>>
export type PatchAdminV1OrgSkuRulesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgSkuRulesId>>>
export type DeleteAdminV1OrgSkuRulesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgSkuRulesId>>>
export type PostAdminV1OrgSkuRulesIdApplyResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgSkuRulesIdApply>>>
export type GetAdminV1OrgSkuRulesIdRunsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSkuRulesIdRuns>>>
export type GetAdminV1OrgSkuRulesIdRunsRunIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgSkuRulesIdRunsRunId>>>
